import { Category } from '@ui/src/components/accounts-selector/types';
import {
  Response,
  BaseTransaction,
  Account,
  apiAbortController,
  IntegrationLog,
} from '@ui/src';
import api from '../../../api/api';
import {
  ExchangeTransactionValidator,
  CreateExchangeTransactionFormValues,
  CalculateTransactionRateFormValues,
  CalculateTransactionRateResult,
  CalculateTransactionRateByIdFormValues,
} from '../types';
import { StatusUpdate } from '../../transactions/type';
import { Balance } from '../../../types/account';

export const exchangeTransactionsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getExchangeList: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-exchange${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeListStatuses: build.query<Response<StatusUpdate>, string>({
      query: (query) => ({
        url: `/v1/transactions-exchange/statuses${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeTransactionById: build.query<
      {
        data: BaseTransaction & {
          exchangeProfitRate?: number;
          exchangeInverseActualRate: number;
          exchangeInverseBaseRate: number;
          exchangeInverseRate: number;
        };
      },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-exchange/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeTransactionValidators: build.query<
      { data: ExchangeTransactionValidator },
      void
    >({
      query: () => ({
        url: '/v1/transactions-exchange/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExchangeTransaction: build.mutation<
      { data: BaseTransaction },
      CreateExchangeTransactionFormValues
    >({
      query: (body) => ({
        url: '/v1/transactions-exchange',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExchangeTransaction: build.mutation<
      {
        data: BaseTransaction & {
          exchangeInverseActualRate: number;
          exchangeInverseBaseRate: number;
          exchangeInverseRate: number;
        };
      },
      { body: Partial<CreateExchangeTransactionFormValues>; id: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-exchange/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    calculateTransactionRates: build.mutation<
      { data: CalculateTransactionRateResult },
      CalculateTransactionRateFormValues
    >({
      query: (body) => ({
        url: '/v1/transactions-exchange/calc',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    calculateTransactionRatesById: build.mutation<
      { data: CalculateTransactionRateResult },
      { id: string; body: CalculateTransactionRateByIdFormValues }
    >({
      query: (data) => ({
        url: `/v1/transactions-exchange/${data.id}/calc`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeAccountsCategories: build.query<Response<Category>, string>({
      query: (query) => ({
        url: `/v1/accounts-categories?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllFromAccountBalances: build.query<Response<Balance>, { id: string }>({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllToAccountBalances: build.query<Response<Balance>, { id: string }>({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportExchangeTransaction: build.mutation<
      any,
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/v1/transactions-exchange/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSyncedTransactions: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-exchange${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeIntegrationLogs: build.query<
      Response<IntegrationLog>,
      { query: string; id: string }
    >({
      query: ({ query, id }) => ({
        url: `/v1/psp-integrations-logs${query}&transactionIds=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExchangeWithoutCorrection: build.query<
      { data: BaseTransaction[] },
      void
    >({
      query: () => ({
        url: '/v1/transactions-exchange/without-corrections',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addMissing: build.mutation<any, string>({
      query: (id) => ({
        url: `/v1/transactions-exchange/without-corrections/${id}/create-correction`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetExchangeListQuery,
  useGetExchangeTransactionValidatorsQuery,
  useLazyGetExchangeTransactionByIdQuery,
  useCreateExchangeTransactionMutation,
  useUpdateExchangeTransactionMutation,
  useCalculateTransactionRatesMutation,
  useGetExchangeAccountsCategoriesQuery,
  useLazyGetExchangeAccountsCategoriesQuery,
  useLazyGetExchangeAccountsListQuery,
  useLazyGetExchangeListStatusesQuery,
  useLazyGetAllFromAccountBalancesQuery,
  useLazyGetAllToAccountBalancesQuery,
  useCalculateTransactionRatesByIdMutation,
  useExportExchangeTransactionMutation,
  useLazyGetSyncedTransactionsQuery,
  useLazyGetExchangeIntegrationLogsQuery,
  useLazyGetExchangeWithoutCorrectionQuery,
  useAddMissingMutation,
} = exchangeTransactionsListApi;

export const {
  endpoints: {
    getExchangeList,
    getExchangeTransactionById,
    getExchangeTransactionValidators,
    createExchangeTransaction,
    updateExchangeTransaction,
    calculateTransactionRates,
    getExchangeAccountsCategories,
    getExchangeAccountsList,
    getExchangeListStatuses,
    getAllFromAccountBalances,
    getAllToAccountBalances,
    calculateTransactionRatesById,
    exportExchangeTransaction,
    getSyncedTransactions,
    getExchangeIntegrationLogs,
    getExchangeWithoutCorrection,
    addMissing,
  },
} = exchangeTransactionsListApi;
